import type { LocationQuery, RouteLocationRaw } from 'vue-router';
import { normalizePropagatedQuery } from '@/helpers/routerUtils';
import { storageService } from '@/services';

/**
 * Regras para o `redirect` propagado na query (merge com storage).
 * Durante jornada iniciada por deeplink, o redirect não deve sobrescrever o destino.
 */
export function shouldFollowPropagatedRedirect(params: {
  isLoggedIn: boolean;
  isDeepLinkJourneyActive: boolean;
  redirect: string | undefined;
  toPath: string;
}): boolean {
  const { isLoggedIn, isDeepLinkJourneyActive, redirect, toPath } = params;
  return Boolean(isLoggedIn && redirect && toPath !== redirect && !isDeepLinkJourneyActive);
}

/**
 * Sem sessão, a rota pedida vai como `redirect` para o `/logout`, que a propaga pelo storage até o
 * login: sem isso, quem toca numa notificação push com a sessão expirada cai em `/dividas` depois
 * de logar. Vai só o caminho, porque a query da entrada já é propagada pelo storage e o `afterEach`
 * só limpa o `redirect` igual ao `path` de destino; com a query junto, o redirect nunca seria limpo.
 */
export function buildLogoutRedirect(toPath: string): RouteLocationRaw {
  if (toPath === '/') return '/logout';
  return { path: '/logout', query: { redirect: toPath } };
}

/**
 * O guard redireciona para o `/logout` antes de o `afterEach` gravar a query da rota pedida. Na
 * entrada a frio o `App.vue` já guardou a query da URL, mas na navegação pelo router (deeplink da
 * notificação com o app aberto) ela se perderia até o login. O `redirect` fica de fora porque o
 * `/logout` recebe o da rota pedida.
 */
export function persistRequestedRouteQuery(query: LocationQuery): void {
  const propagatedQuery = normalizePropagatedQuery(query);
  delete propagatedQuery.redirect;
  storageService.queryString.set(new URLSearchParams(propagatedQuery).toString());
}
